import { CustomError } from "survey-core";
import {
  APPOINTMENT_SETTING_TYPE,
  APPOINTMENT_SETTING_NAME,
} from "./fields/AppointmentSetting";

export const DUPLICATE_TITLE_ERROR_TEXT =
  "Each question must have a unique title.";

function isAppointmentSettingQuestion(question) {
  return question?.getType?.() === APPOINTMENT_SETTING_TYPE;
}

export function getQuestionOutputKey(question) {
  if (isAppointmentSettingQuestion(question)) {
    return APPOINTMENT_SETTING_NAME;
  }
  const title = (question?.title || "").trim();
  return title || question?.name;
}

function getTitleLookupKey(question) {
  if (isAppointmentSettingQuestion(question)) {
    return APPOINTMENT_SETTING_NAME;
  }
  const title = (question?.title || "").trim();
  return title ? title.toLowerCase() : question?.name;
}

function normalizeTitleKey(title) {
  const trimmed = (title || "").trim();
  return trimmed ? trimmed.toLowerCase() : "";
}

/** True when candidateTitle matches another question's title (case-insensitive). */
export function isTitleDuplicateAmongQuestions(survey, question, candidateTitle) {
  if (!survey?.getAllQuestions || !question) return false;

  const normalized = normalizeTitleKey(candidateTitle);
  if (!normalized) return false;

  for (const other of survey.getAllQuestions()) {
    if (other === question || isAppointmentSettingQuestion(other)) continue;
    if (getTitleLookupKey(other) === normalized) return true;
  }
  return false;
}

function hasAnswerValue(value) {
  return value !== undefined && value !== null && value !== "";
}

function findChoiceMatch(question, answer) {
  const choices = question?.choices;
  if (!Array.isArray(choices)) return null;

  const strAnswer = String(answer);
  return choices.find((choice) => {
    if (typeof choice === "object" && choice !== null) {
      return (
        String(choice.value) === strAnswer || String(choice.text) === strAnswer
      );
    }
    return String(choice) === strAnswer;
  });
}

function getChoiceDisplayText(question, rawValue) {
  const item = question?.getItemByValue?.(rawValue);
  if (item?.text) return item.text;

  const match = findChoiceMatch(question, rawValue);
  if (match && typeof match === "object") {
    return match.text || String(match.value);
  }
  if (typeof match === "string") return match;

  return rawValue;
}

function getQuestionOutputValue(question, rawValue) {
  const type = question?.getType?.();

  if (isAppointmentSettingQuestion(question)) {
    return rawValue;
  }

  if (type === "radiogroup" || type === "dropdown") {
    return getChoiceDisplayText(question, rawValue);
  }

  if (type === "checkbox" && Array.isArray(rawValue)) {
    return rawValue
      .map((item) => getChoiceDisplayText(question, item))
      .filter(Boolean)
      .join(", ");
  }

  return question?.displayValue ?? rawValue;
}

function resolveChoiceValueForRestore(question, storedAnswer) {
  const type = question?.getType?.();

  if (type === "checkbox" && typeof storedAnswer === "string") {
    return storedAnswer
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => {
        const match = findChoiceMatch(question, part);
        if (match && typeof match === "object") return match.value;
        return part;
      });
  }

  if (type === "radiogroup" || type === "dropdown") {
    const match = findChoiceMatch(question, storedAnswer);
    if (match && typeof match === "object") return match.value;
  }

  return storedAnswer;
}

export function buildCompletionData(sender) {
  const formData = {};

  sender.getAllQuestions().forEach((question) => {
    const value = sender.data[question.name];
    if (!hasAnswerValue(value)) return;
    formData[getQuestionOutputKey(question)] = getQuestionOutputValue(
      question,
      value
    );
  });

  return formData;
}

export function restoreNameKeyedData(surveyModel, completionData) {
  if (!surveyModel || !completionData) return;

  const nameKeyed = {};

  surveyModel.getAllQuestions().forEach((question) => {
    const outputKey = getQuestionOutputKey(question);
    const storedValue =
      completionData[outputKey] ??
      completionData[question.name];

    if (hasAnswerValue(storedValue)) {
      nameKeyed[question.name] = resolveChoiceValueForRestore(
        question,
        storedValue
      );
    }
  });

  surveyModel.data = nameKeyed;
}

function clearDuplicateTitleErrors(question) {
  if (!question?.errors?.length) return;
  question.errors = question.errors.filter(
    (error) => error?.text !== DUPLICATE_TITLE_ERROR_TEXT
  );
}

export function syncDuplicateTitleErrors(survey) {
  if (!survey?.getAllQuestions) return false;

  const questions = survey
    .getAllQuestions()
    .filter((q) => !isAppointmentSettingQuestion(q));

  const groups = new Map();
  questions.forEach((question) => {
    const key = getTitleLookupKey(question);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(question);
  });

  let hasDuplicates = false;

  questions.forEach((question) => {
    clearDuplicateTitleErrors(question);
    const key = getTitleLookupKey(question);
    const group = groups.get(key) || [];
    if (group.length > 1) {
      hasDuplicates = true;
      question.addError(new CustomError(DUPLICATE_TITLE_ERROR_TEXT));
    }
  });

  return hasDuplicates;
}

export function hasDuplicateTitles(survey) {
  if (!survey?.getAllQuestions) return false;

  const seen = new Set();
  for (const question of survey.getAllQuestions()) {
    if (isAppointmentSettingQuestion(question)) continue;
    const key = getTitleLookupKey(question);
    if (seen.has(key)) return true;
    seen.add(key);
  }
  return false;
}
